'use client'

import { TopicProvider } from './TopicContext'
import SubtopicArticles from './SubtopicArticles'
import ArticleExplorer from './ArticleExplorer'
import ArticlesModal from './ArticlesModal'
import Hr from '@/components/ui/hr'
import Tooltip from '@/components/tooltip'
import { RiInformation2Line } from 'react-icons/ri'
import { getSubtopicBiasLabel } from '@/utils/bias'

const PageSection = ({ sectionTitle, toolTip, hasDivider, biasLabel, children }) => (
  <section>
    <div className="flex gap-2 items-center mb-4 flex-wrap">
      <h2 className="text-xl md:text-2xl font-bold font-serif text-foreground tracking-tight">
        {sectionTitle}
      </h2>
      {biasLabel && (
        <span className={`text-sm ${biasLabel.color}`}>
          {biasLabel.text}
        </span>
      )}
      <Tooltip message={toolTip}>
        <RiInformation2Line className="w-5 h-5 text-muted-foreground hover:text-foreground transition-colors" />
      </Tooltip>
    </div>
    {hasDivider && <Hr />}
    <div className="mt-6">{children}</div>
  </section>
)

export default function TopicContent({ topic, mainStory, coverageAngles }) {
  const hasMainStory = mainStory?.summary
  const hasCoverageAngles = coverageAngles.length > 0

  return (
    <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
        {/* Left Column - Main Story & Coverage Angles */}
        <main className="flex-1 space-y-12 min-w-0">
          {/* Main Story */}
          {hasMainStory && (
            <PageSection
              hasDivider
              sectionTitle="Main Story"
              toolTip="The core narrative of this topic, summarized from reporting across multiple outlets. This captures the key facts that most outlets agree on."
              biasLabel={mainStory.articles?.length > 0 ? getSubtopicBiasLabel(mainStory.articles) : null}
            >
              <div className="prose prose-lg max-w-none">
                <p className="lede">{mainStory.summary}</p>
              </div>
              {mainStory.articles?.length > 0 && (
                <SubtopicArticles
                  articles={mainStory.articles}
                  sectionId="main-story"
                  sectionName="Main Story"
                />
              )}
            </PageSection>
          )}

          {/* Coverage Angles - Other subtopics */}
          {hasCoverageAngles && (
            <PageSection
              hasDivider
              sectionTitle="Coverage Angles"
              toolTip="Different angles and perspectives that emerge naturally from how outlets cover this topic. These aren't forced into left vs. right boxes—they reflect what different outlets choose to emphasize."
            >
              <div className="space-y-10">
                {coverageAngles.map((subtopic, index) => {
                  const biasLabel = subtopic.articles?.length > 0
                    ? getSubtopicBiasLabel(subtopic.articles)
                    : null
                  return (
                    <div key={subtopic.id || index}>
                      <div className="border-l-2 border-accent pl-4 mb-4">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <h3 className="font-serif font-bold text-lg">
                            {subtopic.title}
                          </h3>
                          {biasLabel && (
                            <span className={`text-sm ${biasLabel.color}`}>
                              {biasLabel.text}
                            </span>
                          )}
                        </div>
                        <p className="text-muted-foreground">
                          {subtopic.summary}
                        </p>
                      </div>
                      {subtopic.articles?.length > 0 && (
                        <SubtopicArticles
                          articles={subtopic.articles}
                          sectionId={`angle-${index + 1}`}
                          sectionName={subtopic.title}
                        />
                      )}
                    </div>
                  )
                })}
              </div>
            </PageSection>
          )}
        </main>

        {/* Right Column - Article Explorer Sidebar (desktop only) */}
        <aside className="hidden lg:block lg:w-[340px] flex-shrink-0">
          <div className="lg:sticky lg:top-24">
            <ArticleExplorer articles={topic.articles} />
          </div>
        </aside>

        {/* Mobile Articles Modal */}
        <ArticlesModal />
      </div>
  )
}
